import React, { useEffect, useId, useState } from 'react';
import { ClientOnly } from '~/design-system/components/ClientOnly/ClientOnly';
import { demoDurationMs, HeroPitchDemo } from './pitch/HeroPitchDemo';
import { HERO_SCRIPTS } from './pitch/heroScripts';
import { usePrefersReducedMotion } from '~/landing_page/components/FeaturesSection/usePrefersReducedMotion';
import { cn } from '~/lib/utils';
import { MorphingWord } from './MorphingWord';
import styles from './HeroSectionV2.module.css';

/**
 * Landing v2 hero — Paper "02 Hero · dark" in the Landing page file, as it stands on 2026-09-03.
 *
 * Centred headline and the two live CTA pills, then the use-case tabs centred just above the product
 * window, and in the window the Deck 1.5 workspace writing that tab's deck (`HeroPitchDemo`: the same
 * assembly the deck route runs, driven by one script per tab in `heroScripts.ts`). The window has no chrome of its own: no title
 * bar, no lights, no home button.
 *
 * The workspace is client-only: it is the heaviest thing on the page and the landing must never
 * carry it in the server render.
 */


/* The same eight words the current hero types out (HeroSection.tsx); here they melt into each other. */
const DEFAULT_HEADLINE_WORDS = [
  'work',
  'ideas',
  'research',
  'roadmap',
  'launch',
  'findings',
  'quarter',
  'side project',
] as const;

export interface HeroSectionV2Props {
  className?: string;
  /** Everything before the morphing word. */
  headlinePrefix?: string;
  /** The words that melt into one another at the end of the headline. */
  headlineWords?: readonly string[];
  webCtaLabel?: string;
  webCtaHref?: string;
  macCtaLabel?: string;
  macCtaHref?: string;
}

export function HeroSectionV2({
  className,
  headlinePrefix = 'Deck presents your',
  headlineWords = DEFAULT_HEADLINE_WORDS,
  webCtaLabel = 'Continue in web',
  webCtaHref = '/sign-in',
  macCtaLabel = 'Download Mac app',
  macCtaHref = '/download/mac',
}: HeroSectionV2Props) {
  const baseId = useId();
  const [active, setActive] = useState(0);
  // Bumped on every activation, including a click on the tab that is already open. It keys the
  // fill, so remounting replays the sweep from zero instead of leaving it mid-travel.
  const [run, setRun] = useState(0);
  const reducedMotion = usePrefersReducedMotion();
  // Each tab holds until its deck is written and has been on screen a moment.
  const dwellMs = demoDurationMs(HERO_SCRIPTS[active]);

  const goTo = (index: number) => {
    setActive(index);
    setRun((r) => r + 1);
  };

  // The strip advances on its own until motion is reduced; clicking still works either way.
  useEffect(() => {
    if (reducedMotion) return;
    const timer = setTimeout(() => goTo((active + 1) % HERO_SCRIPTS.length), dwellMs);
    return () => clearTimeout(timer);
  }, [active, run, reducedMotion, dwellMs]);

  // Assistive tech gets one static sentence; the morphing copies are hidden from it.
  const headlineLabel = `${headlinePrefix} ${headlineWords[0] ?? ''}`.trim();

  return (
    <section className={cn(styles.hero, className)} data-name="HeroSectionV2">
      <div className={styles.heroContainer}>
        <div className={styles.headerContent}>
          <h1 className={styles.headline} aria-label={headlineLabel}>
            <span className={styles.headlineText} aria-hidden="true">
              {headlinePrefix}{' '}
              <MorphingWord texts={headlineWords} />
            </span>
          </h1>

          <div className={styles.actions}>
            <a className={styles.webCta} href={webCtaHref}>
              <img
                className={styles.ctaIcon}
                src="/landing/hero/icon-web.svg"
                alt=""
                width={18}
                height={18}
              />
              {webCtaLabel}
            </a>
            <a className={styles.macCta} href={macCtaHref}>
              <img
                className={styles.ctaIcon}
                src="/landing/hero/icon-mac.svg"
                alt=""
                width={18}
                height={18}
              />
              {macCtaLabel}
            </a>
          </div>
        </div>

        <div className={styles.visual}>
          <div
            className={styles.tabs}
            role="tablist"
            aria-label="Deck examples"
            // Drives the fill's duration, so the sweep and the handover are the same number.
            style={{ '--hero-dwell': `${dwellMs}ms` } as React.CSSProperties}
          >
            {HERO_SCRIPTS.map(({ id, label }, i) => {
              const isActive = i === active;
              return (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  id={`${baseId}-tab-${i}`}
                  className={cn(styles.tab, isActive && styles.tabActive)}
                  aria-selected={isActive}
                  tabIndex={isActive ? 0 : -1}
                  onClick={() => goTo(i)}
                >
                  {/* Sits under the label and sweeps across over the dwell, so the pill reads as
                      filling up — how much of this file's time is left. */}
                  {isActive && !reducedMotion && (
                    <span key={run} className={styles.tabProgress} aria-hidden="true" />
                  )}
                  <span className={styles.tabLabel}>{label}</span>
                </button>
              );
            })}
          </div>

          <div className={styles.frame} data-name="HeroFrame">
            <ClientOnly fallback={<div className={styles.workspaceFallback} aria-hidden />}>
              <div className={styles.workspace}>
                {/* Keyed on the activation, so every tab (and a click on the open one) writes its deck from the start. */}
                <HeroPitchDemo key={run} theme="dark" script={HERO_SCRIPTS[active]} />
              </div>
            </ClientOnly>
          </div>
        </div>
      </div>
    </section>
  );
}

HeroSectionV2.displayName = 'HeroSectionV2';
